import React, { useState, useEffect,useCallback } from 'react';
import { useParams, useNavigate, useSearchParams,useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiClock, FiAlertCircle } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';

const QuizAttempt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const classroomId = searchParams.get('classroom') || location.state?.classroomId;

  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: quiz, isLoading } = useQuery(
    {queryKey:['quiz', id],
    queryFn:() => axios.get(`/api/quizzes/${id}`).then(res => res.data.quiz)
  });

  useEffect(() => {
    if (quiz && quiz.duration) {
      setTimeLeft(quiz.duration * 60); // Convert minutes to seconds
    }
  }, [quiz]);



  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (questionId, answerIndex) => {
  setAnswers(prev => ({
    ...prev,
    [questionId.toString()]: answerIndex  // Convert to string
  }));
};

  const handleSubmit = useCallback(async () => {
    if (submitting) return;

    const unansweredCount = quiz.questions.length - Object.keys(answers).length;
    if (unansweredCount > 0) {
      if (!window.confirm(`You have ${unansweredCount} unanswered questions. Submit anyway?`)) {
        return;
      }
    }

    setSubmitting(true);
    try {
      console.log("Submitting  with classroomId:", classroomId);
      const response = await axios.post('/api/quizzes/submit', {
        quizId: id,
        classroomId: classroomId,
        answers
      });
      sessionStorage.setItem('quizResults', JSON.stringify(response.data.results));
      navigate(`/quizzes/${id}/result`,
        {
          state: {
            results: response.data.results,
        },
          replace: true
        });

    } catch (error) {
      if (error.response?.data?.error === 'Quiz already attempted') {
        toast.error('You have already attempted this quiz');
        navigate('/quizzes');
      } else {
        toast.error('Failed to submit quiz');
      }
    } finally {
      setSubmitting(false);
    }
  }, [submitting, quiz, answers, id, classroomId, navigate]);

    useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft,handleSubmit]);

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!quiz) {
    return <div>Quiz not found</div>;
  }

  const question = quiz.questions[currentQuestion];
  const isLastQuestion = currentQuestion === quiz.questions.length - 1;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="card mb-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{quiz.title}</h1>
          <div className="flex items-center space-x-4">
            <div className="flex items-center text-gray-600">
              <FiClock className="mr-2" />
              <span className={`font-mono text-lg ${timeLeft < 300 ? 'text-red-600' : ''}`}>
                {formatTime(timeLeft)}
              </span>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Question {currentQuestion + 1} of {quiz.questions.length}</span>
            <span>{question.points} points</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / quiz.questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          {question.question}
        </h2>

        <div className="space-y-3">
          {question.options.map((option, index) => (
            <label
              key={index}
              className={`block p-4 rounded-lg border-2 cursor-pointer transition-all ${
                answers[question._id] === index
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name={`question-${question._id}`}
                value={index}
                checked={answers[question._id] === index}
                onChange={() => handleAnswerSelect(question._id, index)}
                className="sr-only"
              />
              <span className="text-gray-900">{option}</span>
            </label>
          ))}
        </div>

        <div className="flex justify-between mt-8">
          <button
            onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
            disabled={currentQuestion === 0}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="flex items-center space-x-2">
            {quiz.questions.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentQuestion(index)}
                className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                  index === currentQuestion
                    ? 'bg-primary-600 text-white'
                    : answers[quiz.questions[index]._id] !== undefined
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>

          {isLastQuestion ? (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="btn-primary"
            >
              {submitting ? <LoadingSpinner size="small" /> : 'Submit Quiz'}
            </button>
          ) : (
            <button
              onClick={() => setCurrentQuestion(prev => Math.min(quiz.questions.length - 1, prev + 1))}
              className="btn-primary"
            >
              Next
            </button>
          )}
        </div>
      </div>

      {timeLeft < 300 && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center">
          <FiAlertCircle className="text-yellow-600 mr-2" />
          <span className="text-yellow-800">Less than 5 minutes remaining!</span>
        </div>
      )}
    </div>
  );
};

export default QuizAttempt;